import { ArrowLeft, User, LogOut } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import React, { useState, useEffect } from "react";
import { useEngagement } from "@/contexts/EngagementContext";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { getDOC_LIBRARY, getFY_YEAR } from "@/utils/sharepoint-env";
import logoNameDark from "/logo_name_dark.png";

interface TopHeaderProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  onToggleLibraries?: () => void;
}

export function TopHeader({
  searchTerm,
  setSearchTerm,
  onToggleLibraries,
}: TopHeaderProps) {
  const { currentEngagement } = useEngagement();
  const { isArchivalMode } = useArchivalMode();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isEngagementDetailPage = location.pathname.startsWith("/engagement/");
  
  const [docLibrary, setDocLibrary] = useState<string>("");
  const [fyYear, setFyYear] = useState<string>("");
  
  // Load doc_library and fy_year from SharePoint config
  useEffect(() => {
    const loadSharePointConfig = () => {
      const docLib = getDOC_LIBRARY();
      const fy = getFY_YEAR();
      setDocLibrary(docLib || "");
      setFyYear(fy || "");
    };
    
    loadSharePointConfig();
    
    // Also listen for config changes (when user selects different engagement)
    const interval = setInterval(loadSharePointConfig, 1000);
    
    return () => clearInterval(interval);
  }, [currentEngagement]);

  return (
    <div className="border-b border-white/10 bg-black/40 backdrop-blur-md">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Left section: Logo with text and Engagement name */}
        <div className="flex items-center gap-4">
          <div className="flex items-center">
            <img 
              src={logoNameDark} 
              alt="Verity AI" 
              className="h-10 object-contain"
            />
          </div>
          <div className="h-6 w-px bg-white/20"></div>
          <div className="flex flex-col">
            <span className="text-lg font-bold text-white">
              {docLibrary || "No Library"}
              {isArchivalMode && (
                <Badge className="ml-3 bg-yellow-600/20 text-yellow-400 border-yellow-500/30">
                  Archival
                </Badge>
              )}
            </span>
            {fyYear && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {fyYear}
              </p>
            )}
          </div>
        </div>

        {/* Right section: Buttons and User */}
        <div className="flex items-center gap-4">
          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {isEngagementDetailPage && !isArchivalMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ to: "/" })}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Engagements
              </Button>
            )}
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-white/20"></div>

          {/* User name - styled more professionally */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-purple-500/20 border border-white/20">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
              <span className="text-sm font-semibold text-white">
                {user?.user_name || "User"}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await logout();
                window.location.reload();
              }}
              className="flex items-center gap-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/20 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              title="Log Out"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
              <span>Choose Different File</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
